import { useEffect, useMemo, useState } from 'react';
import { CalendarClock, ChevronDown, Loader2, RefreshCw, X } from 'lucide-react';
import { toast } from 'sonner';
import { confirmDialog } from '../../../shared/ui/ConfirmHost';
import { splitQueue } from '../lib/splitQueue';

import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { TableSkeleton } from '../../../shared/ui/TableSkeleton';
import { useCachedQuery } from '../../../shared/lib/queryCache';

import {
  cancelScheduled,
  listScheduled,
  type ScheduledItem,
  type ScheduledStatus,
} from '../../../shared/api/scheduler.api';

const STATUS_LABEL: Record<ScheduledStatus, string> = {
  pending: 'Ожидает',
  running: 'Публикуется',
  done: 'Опубликовано',
  failed: 'Ошибка',
  cancelled: 'Отменено',
};

const STATUS_VARIANT: Record<ScheduledStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  pending: 'outline',
  running: 'default',
  done: 'secondary',
  failed: 'destructive',
  cancelled: 'secondary',
};

function formatRunAt(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
}

/** «через 12 мин» / «5 мин назад» — чтобы не считать время в уме. */
function relative(iso: string): string {
  const diffMin = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
  const abs = Math.abs(diffMin);
  const unit = abs < 60 ? `${abs} мин` : abs < 1440 ? `${Math.round(abs / 60)} ч` : `${Math.round(abs / 1440)} дн`;
  return diffMin >= 0 ? `через ${unit}` : `${unit} назад`;
}

export function ScheduledSection(): JSX.Element {
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  // Очередь показываем из кэша и обновляем следом: возвращение на вкладку не
  // должно начинаться с пустого экрана.
  const {
    data,
    error: loadError,
    isLoading,
    refresh: load,
  } = useCachedQuery<ScheduledItem[]>('scheduled:list', listScheduled, { ttlMs: 30_000 });

  const error = loadError ? loadError.message || 'Не удалось загрузить очередь' : null;

  // Сверху — что ещё впереди, по времени выхода (ближайшее первым); ниже,
  // свёрнутой, — история: вышедшее, упавшее и отменённое, свежее первым.
  const { upcoming, history } = useMemo(() => splitQueue(data ?? []), [data]);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    // Планировщик тикает раз в минуту — обновляемся в том же темпе.
    const timer = setInterval(load, 60_000);
    return () => clearInterval(timer);
  }, [load]);

  async function handleCancel(item: ScheduledItem): Promise<void> {
    const ok = await confirmDialog({
      title: 'Отменить отложенную публикацию?',
      description: `${item.kind === 'reel' ? 'Рилс' : 'Пост'} на ${formatRunAt(item.run_at)}${item.label ? ` · ${item.label}` : ''} не выйдет. Вернуть в очередь можно только новой публикацией.`,
      confirmText: 'Отменить публикацию',
      cancelText: 'Оставить',
      destructive: true,
    });
    if (!ok) return;
    setCancellingId(item.id);
    try {
      await cancelScheduled(item.id);
      toast.success('Публикация отменена');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось отменить');
    } finally {
      setCancellingId(null);
    }
  }

  const pending = upcoming.filter((i) => i.status === 'pending').length;

  const renderTable = (rows: ScheduledItem[]) => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Время</TableHead>
          <TableHead>Тип</TableHead>
          <TableHead>Аккаунты</TableHead>
          <TableHead>Статус</TableHead>
          <TableHead className="w-[80px]" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((item) => (
          <TableRow key={item.id}>
            <TableCell className="whitespace-nowrap">
              <div>{formatRunAt(item.run_at)}</div>
              {item.status === 'pending' && (
                <div className="text-xs text-muted-foreground">{relative(item.run_at)}</div>
              )}
            </TableCell>
            <TableCell>{item.kind === 'reel' ? 'Рилс' : 'Пост'}</TableCell>
            <TableCell className="max-w-[320px] truncate">{item.label ?? '—'}</TableCell>
            <TableCell>
              <Badge variant={STATUS_VARIANT[item.status]}>{STATUS_LABEL[item.status]}</Badge>
              {item.last_error && (
                <div className="text-xs text-destructive mt-1 max-w-[320px] truncate" title={item.last_error}>
                  {item.last_error}
                </div>
              )}
              {item.attempts > 1 && (
                <div className="text-xs text-muted-foreground mt-1">попыток: {item.attempts}</div>
              )}
            </TableCell>
            <TableCell>
              {/* Отменить можно только ожидающую: остальное уже необратимо. */}
              {item.status === 'pending' && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1"
                  disabled={cancellingId === item.id}
                  onClick={() => void handleCancel(item)}
                >
                  {cancellingId === item.id ? <Loader2 className="size-4 animate-spin" /> : <X className="size-4" />}
                  Отменить
                </Button>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CalendarClock className="size-5" />
              Отложенные публикации
            </CardTitle>
            <CardDescription>
              {pending > 0
                ? `Ожидают публикации: ${pending}. Планировщик забирает записи раз в минуту.`
                : 'Ожидающих публикаций нет.'}
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" className="gap-2" onClick={() => void load()}>
            <RefreshCw className="size-4" />
            Обновить
          </Button>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Время</TableHead>
                  <TableHead>Тип</TableHead>
                  <TableHead>Аккаунты</TableHead>
                  <TableHead>Статус</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableSkeleton columns={5} rows={4} />
              </TableBody>
            </Table>
          ) : error ? (
            <div className="py-10 text-center text-sm text-destructive">{error}</div>
          ) : upcoming.length === 0 && history.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Очередь пуста. Время публикации задаётся в форме создания рилса или поста — шаг «Когда публикуем».
            </div>
          ) : (
            <div className="space-y-6">
              {upcoming.length > 0 ? (
                renderTable(upcoming)
              ) : (
                <p className="py-4 text-center text-sm text-muted-foreground">Ожидающих публикаций нет.</p>
              )}
              {history.length > 0 && (
                <div className="space-y-2">
                  <button
                    type="button"
                    className="flex items-center gap-1 text-sm font-medium"
                    onClick={() => setHistoryOpen((v) => !v)}
                    aria-expanded={historyOpen}
                  >
                    <ChevronDown className={`size-4 transition-transform ${historyOpen ? 'rotate-180' : ''}`} />
                    История · {history.length}
                  </button>
                  {historyOpen && renderTable(history)}
                </div>
              )}
            </div>
          )}
        </CardContent>
    </Card>
  );
}
